import { qualityOrder, qualityPresets } from '../config';
import { elements } from '../../../dom';
import type { AspectId, PaletteId, QualityId, TemplateId } from '../types';
import { setActive, setActiveBySelector } from '../../../utils/button-group';

type AdvancedControlsOptions = {
  onAspectChange: (aspect: AspectId) => void;
  onPaletteChange: (palette: PaletteId) => void;
  onQualityChange: (quality: QualityId) => void;
  onTemplateChange: (template: TemplateId) => void;
};

export function createAdvancedControls({
  onAspectChange,
  onPaletteChange,
  onQualityChange,
  onTemplateChange,
}: AdvancedControlsOptions) {
  let setupDisabled = false;

  document.querySelectorAll<HTMLButtonElement>('[data-aspect]').forEach((button) => {
    button.addEventListener('click', () => {
      if (setupDisabled) {
        return;
      }

      const aspect = button.dataset.aspect as AspectId;
      setActive('[data-aspect]', button);
      onAspectChange(aspect);
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-template]').forEach((button) => {
    button.addEventListener('click', () => {
      if (setupDisabled) {
        return;
      }

      const template = button.dataset.template as TemplateId;
      setActive('[data-template]', button);
      onTemplateChange(template);
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-palette]').forEach((button) => {
    button.addEventListener('click', () => {
      if (setupDisabled) {
        return;
      }

      const palette = button.dataset.palette as PaletteId;
      setActive('[data-palette]', button);
      onPaletteChange(palette);
    });
  });

  elements.qualitySlider.addEventListener('input', () => {
    if (setupDisabled) {
      return;
    }

    const quality = qualityFromSliderValue(elements.qualitySlider.value);
    if (quality) {
      updateQualitySlider(quality);
      onQualityChange(quality);
    }
  });

  return {
    collapse() {
      // The design/export controls are always visible now.
    },
    setActiveAspect(aspect: AspectId) {
      setActiveBySelector(`[data-aspect="${aspect}"]`, '[data-aspect]');
    },
    setActiveQuality(quality: QualityId) {
      updateQualitySlider(quality);
    },
    setActiveTemplate(template: TemplateId) {
      setActiveBySelector(`[data-template="${template}"]`, '[data-template]');
    },
    setSetupDisabled(isDisabled: boolean) {
      setupDisabled = isDisabled;
      elements.advancedControls.classList.toggle('is-disabled', isDisabled);
      elements.qualitySlider.disabled = isDisabled;
      document.querySelectorAll<HTMLButtonElement>('[data-aspect], [data-template], [data-palette]').forEach((button) => {
        button.disabled = isDisabled;
      });
    },
    updateVisibility(_hasAudioFile?: boolean) {
      elements.advancedControls.hidden = false;
    },
  };
}

function qualityFromSliderValue(value: string) {
  const index = Number(value);
  return Number.isInteger(index) ? qualityOrder[index] : undefined;
}

function updateQualitySlider(quality: QualityId) {
  const index = qualityOrder.indexOf(quality);
  if (index < 0) {
    return;
  }

  const preset = qualityPresets[quality];
  elements.qualitySlider.value = String(index);
  elements.qualitySlider.setAttribute('aria-valuetext', `${preset.label}, ${preset.description}`);
}
